/**
 * In-content Live Ops placeholder while hub auth + iframe hydrate.
 * Shell-first polish: user already sees Command chrome; no second full-screen boot.
 */
export function LiveOpsSkeleton({
  host,
  error = false,
  onRetry,
}: {
  host?: string;
  error?: boolean;
  onRetry?: () => void;
}) {
  return (
    <div
      className="absolute inset-0 z-10 flex flex-col bg-zinc-100 dark:bg-zinc-950"
      role="status"
      aria-live="polite"
      aria-busy={!error}
    >
      {/* Match Live Ops: left rail + main + optional right */}
      <div className="flex min-h-0 flex-1">
        {/* Sidebar skeleton */}
        <aside className="hidden w-64 shrink-0 flex-col border-r border-zinc-200 bg-white dark:border-white/10 dark:bg-zinc-900 md:flex">
          <div className="border-b border-zinc-100 p-3 dark:border-white/5">
            <div className="h-8 w-full animate-pulse rounded-lg bg-zinc-200 dark:bg-zinc-800" />
          </div>
          <div className="space-y-2 p-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className="h-9 animate-pulse rounded-lg bg-zinc-100 dark:bg-zinc-800/80"
                style={{ width: `${72 + (i % 3) * 8}%`, animationDelay: `${i * 80}ms` }}
              />
            ))}
          </div>
        </aside>

        {/* Chat skeleton */}
        <main className="flex min-w-0 flex-1 flex-col">
          <div className="flex h-12 items-center gap-3 border-b border-zinc-200 bg-white px-4 dark:border-white/10 dark:bg-zinc-900">
            <div className="h-4 w-28 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
            <div className="ml-auto h-4 w-16 animate-pulse rounded bg-zinc-100 dark:bg-zinc-800" />
          </div>
          <div className="flex flex-1 flex-col justify-end gap-4 p-4 sm:p-6">
            <div className="flex gap-3">
              <div className="h-8 w-8 shrink-0 animate-pulse rounded-full bg-zinc-200 dark:bg-zinc-800" />
              <div className="space-y-2">
                <div className="h-3 w-20 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800" />
                <div className="h-16 w-56 max-w-[70vw] animate-pulse rounded-2xl bg-zinc-200 dark:bg-zinc-800 sm:w-72" />
              </div>
            </div>
            <div className="flex flex-row-reverse gap-3">
              <div className="h-8 w-8 shrink-0 animate-pulse rounded-full bg-orange-200/80 dark:bg-orange-500/20" />
              <div className="h-12 w-48 max-w-[60vw] animate-pulse rounded-2xl bg-orange-100 dark:bg-orange-500/15 sm:w-64" />
            </div>
            <div className="flex gap-3">
              <div className="h-8 w-8 shrink-0 animate-pulse rounded-full bg-zinc-200 dark:bg-zinc-800" />
              <div className="h-10 w-40 animate-pulse rounded-2xl bg-zinc-200 dark:bg-zinc-800" />
            </div>
          </div>
          <div className="border-t border-zinc-200 bg-white p-3 dark:border-white/10 dark:bg-zinc-900">
            <div className="h-11 w-full animate-pulse rounded-2xl bg-zinc-100 dark:bg-zinc-800" />
          </div>
        </main>
      </div>

      {/* Status strip — calm, not a second splash screen */}
      <div className="pointer-events-none absolute inset-x-0 bottom-20 flex justify-center px-4 md:bottom-24">
        <div
          className={`pointer-events-auto flex max-w-sm items-center gap-3 rounded-2xl border px-4 py-2.5 shadow-lg backdrop-blur-md ${
            error
              ? "border-red-500/30 bg-zinc-950/90 text-red-200"
              : "border-white/10 bg-zinc-950/85 text-zinc-300"
          }`}
        >
          {!error ? (
            <span
              className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-orange-400/30 border-t-orange-400"
              aria-hidden
            />
          ) : (
            <span className="h-2 w-2 shrink-0 rounded-full bg-red-400" aria-hidden />
          )}
          <div className="min-w-0 text-left">
            <p className="text-xs font-medium text-white">
              {error ? "Couldn’t connect to your hub" : "Connecting Live Ops"}
            </p>
            <p className="truncate font-mono text-[10px] text-zinc-500">
              {error
                ? "Use Reconnect hub in the header"
                : host || "hub.opengateways.xyz"}
            </p>
          </div>
          {error && onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className="shrink-0 rounded-full border border-orange-400/40 px-2.5 py-1 text-[11px] font-medium text-orange-300 hover:bg-orange-500/10"
            >
              Retry
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
